import {getApiTypeFromGraphQLTypename} from 'nutshell-core/utils';

import type {ThreadType} from 'nutshell-graphql-types';

import {EditableLinkedEntity} from '../../linked-entity/editable-linked-entity';
import {Body} from '../../typography/body';

import {isUnidentifiedRecipient} from '../helpers';
import type {Recipient} from '../types';

import {ThreadHeaderAvatar} from './thread-header-avatar';
import {ThreadHeaderTimestamp} from './thread-header-timestamp';

import styles from './recipient-display.module.css';

type Props = {
    recipient: Recipient;
    isLinkable?: boolean;
    isSmall?: boolean;
    onSaveContactName?: (name: string) => void;
    isLoadingChannelEvents?: boolean;
    shouldShowStatusDot?: boolean;
    threadType?: ThreadType | null | undefined;
};

function getStubbedEntityDetails(id: string) {
    switch (id) {
        case 'shelly':
            return {
                id: 'shelly',
                name: 'Shelly',
                description: 'Your friendly guide to Nutshell SMS',
            };
        default:
            return null;
    }
}

function getNameToDisplay(name: string) {
    const nameIsPhoneNumber = name && typeof name === 'string' && name.match(/^\+?[1-9]\d{1,14}$/);

    return nameIsPhoneNumber ? '' : name;
}

function StubbedEntityDisplay({name, description}: {name: string; description: string}) {
    return (
        <div className='flex flex-dir-col font-size-normal gap-2'>
            <Body size='normal' color='dark' isBold={true}>
                {name}
            </Body>
            <Body size='normal' color='light' isItalic={true}>
                {description}
            </Body>
        </div>
    );
}

export function RecipientDisplay(props: Props) {
    const {isLinkable = true} = props;
    const {
        id,
        name,
        avatarUrl,
        initials,
        phoneNumber,
        primaryEmailAddress,
        htmlUrlPath,
        __typename,
        lastSeenTime,
        isActive,
    } = props.recipient;
    const stubbedEntityDetails = getStubbedEntityDetails(id);
    const type = getApiTypeFromGraphQLTypename(__typename);
    const nameToDisplay = getNameToDisplay(name);

    return (
        <div className='flex align-center mr-16'>
            <div className='flex align-center mr-8'>
                <ThreadHeaderAvatar
                    size={props.isSmall ? 32 : 40}
                    recipientId={id}
                    initials={initials}
                    type={type}
                    avatarUrl={avatarUrl}
                    isActive={isActive}
                    shouldShowStatusDot={props.shouldShowStatusDot}
                />
            </div>
            {stubbedEntityDetails ? (
                <StubbedEntityDisplay {...stubbedEntityDetails} />
            ) : (
                <div
                    className={`flex flex-dir-col font-size-normal ${
                        props.isSmall ? '' : ' gap-2'
                    }`}
                >
                    <div className={styles['recipient-name']}>
                        <EditableLinkedEntity
                            useEntityColor={!isUnidentifiedRecipient(nameToDisplay)}
                            stopPropagation={true}
                            shouldClientRoute={true}
                            isLinkable={isLinkable}
                            htmlUrlPath={htmlUrlPath}
                            type={type}
                            name={nameToDisplay}
                            id={id}
                            handleSave={props.onSaveContactName}
                            disableEditing={!props.onSaveContactName || type !== 'contacts'}
                        />
                    </div>
                    {(primaryEmailAddress || phoneNumber) && (
                        <div
                            className={styles['contact-info']}
                            title={primaryEmailAddress || phoneNumber}
                        >
                            <Body size={props.isSmall ? 'small' : 'normal'}>
                                {primaryEmailAddress || phoneNumber}
                            </Body>
                        </div>
                    )}
                    {lastSeenTime && (
                        <ThreadHeaderTimestamp lastSeenTime={lastSeenTime} isActive={isActive} />
                    )}
                    {props.isLoadingChannelEvents &&
                        !lastSeenTime &&
                        props.threadType === 'CHAT' && <div className={styles.loading} />}
                </div>
            )}
        </div>
    );
}
